import { useTheme, useThemeMode } from "../../styles/theme";
import { useApp } from "../../context/AppContext";
import { CURRENT_KEY, defaultState } from "../../utils/data";
import SectionTitle from "../../components/SectionTitle";

export default function Settings() {
  const S = useTheme();
  const c = S.colors;
  const { mode, setMode } = useThemeMode();
  const {
    data, save, showToast,
    importStatus, setImportStatus,
    exportData, importData, replaceAllData,
  } = useApp();

  const summary = [
    { label: "Sales",            count: data.sales.length,                   color: c.primary,     icon: "💰" },
    { label: "Purchases",        count: data.purchases.length,                color: c.warn,        icon: "🏪" },
    { label: "Expenses",         count: (data.expenses || []).length,         color: c.purple,      icon: "💸" },
    { label: "Salaries",         count: data.salaries.length,                 color: c.indigo,      icon: "👤" },
    { label: "Debts",            count: (data.debts || []).length,            color: c.dangerLight, icon: "🔴" },
    { label: "Opening Balances", count: (data.openingBalances || []).length,  color: c.info,        icon: "🏦" },
  ];

  return (
    <div>
      {/* Appearance */}
      <SectionTitle>Appearance</SectionTitle>
      <div style={S.settingsCard}>
        <div style={S.settingsCardIcon}>{mode === "dark" ? "🌙" : "☀️"}</div>
        <div style={S.settingsCardBody}>
          <div style={S.settingsCardTitle}>Theme</div>
          <div style={S.settingsCardDesc}>
            Choose how ProfitLens looks. Your preference is saved to this browser.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            {[
              { k: "dark",  l: "🌙 Dark" },
              { k: "light", l: "☀️ Light" },
            ].map(opt => (
              <button
                key={opt.k}
                onClick={() => setMode(opt.k)}
                style={{
                  ...S.strategyBtn,
                  ...(mode === opt.k ? S.strategyActive : {}),
                  padding: "8px 16px",
                  fontSize: 13,
                }}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SectionTitle style={{ marginTop: 28 }}>Data Backup &amp; Restore</SectionTitle>
      <p style={S.tabDesc}>
        Export your data as a JSON file to back it up or move to a new installation.
      </p>

      {/* Record counts */}
      <div style={S.settingsSummary}>
        {summary.map(({ label, count, color, icon }) => (
          <div key={label} style={{ ...S.settingsSummaryCard, borderColor: color + "44" }}>
            <div style={{ fontSize: 20 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color }}>{count}</div>
            <div style={{ fontSize: 11, color: c.textFaint }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Export */}
      <SectionTitle style={{ marginTop: 28 }}>📤 Export Data</SectionTitle>
      <div style={S.settingsCard}>
        <div style={S.settingsCardIcon}>📤</div>
        <div style={S.settingsCardBody}>
          <div style={S.settingsCardTitle}>Download Backup File</div>
          <div style={S.settingsCardDesc}>
            Downloads all your data as a <code style={S.code}>.json</code> file.
            Save this safely — you can restore it on any device.
          </div>
          <button style={{ ...S.submitBtn, marginTop: 14 }} onClick={exportData}>
            ⬇ Download profitlens-backup.json
          </button>
        </div>
      </div>

      {/* Import & Merge */}
      <SectionTitle style={{ marginTop: 28 }}>📥 Import &amp; Merge</SectionTitle>
      <div style={S.settingsCard}>
        <div style={S.settingsCardIcon}>🔀</div>
        <div style={S.settingsCardBody}>
          <div style={S.settingsCardTitle}>Merge with existing data</div>
          <div style={S.settingsCardDesc}>
            Imports records and <strong>merges</strong> them with current data.
            Duplicate IDs are skipped — nothing gets overwritten.
          </div>
          <label style={S.fileLabel}>
            <span>📂 Choose backup .json file to merge</span>
            <input type="file" accept=".json" style={{ display: "none" }}
              onChange={e => { setImportStatus(null); importData(e.target.files[0]); e.target.value = ""; }} />
          </label>
        </div>
      </div>

      {/* Replace All */}
      <SectionTitle style={{ marginTop: 28 }}>♻️ Replace All Data</SectionTitle>
      <div style={{ ...S.settingsCard, borderColor: c.dangerBorder }}>
        <div style={S.settingsCardIcon}>⚠️</div>
        <div style={S.settingsCardBody}>
          <div style={{ ...S.settingsCardTitle, color: c.dangerLight }}>Replace everything with backup file</div>
          <div style={S.settingsCardDesc}>
            <strong style={{ color: c.dangerLight }}>This will delete all current data</strong> and replace it entirely with the backup file.
          </div>
          <label style={{ ...S.fileLabel, borderColor: c.dangerBorder, color: c.dangerLight }}>
            <span>📂 Choose backup .json file to restore</span>
            <input type="file" accept=".json" style={{ display: "none" }}
              onChange={e => {
                if (!window.confirm("This will DELETE all current data and replace with the file. Are you sure?")) return;
                setImportStatus(null);
                replaceAllData(e.target.files[0]);
                e.target.value = "";
              }} />
          </label>
        </div>
      </div>

      {/* Import status */}
      {importStatus && (
        <div style={{
          ...S.importStatus,
          background: importStatus.type === "error" ? c.badgeDueBg : c.okBgStrong,
          borderColor: importStatus.type === "error" ? c.dangerBorder : c.okBorder,
          color: importStatus.type === "error" ? c.dangerLight : c.primary,
        }}>
          {importStatus.type === "error" ? "❌ " : "✅ "}
          {importStatus.msg}
        </div>
      )}

      {/* Storage info */}
      <SectionTitle style={{ marginTop: 28 }}>🔄 Storage Info</SectionTitle>
      <div style={{ ...S.settingsCard, borderColor: c.infoBorder }}>
        <div style={S.settingsCardIcon}>🔄</div>
        <div style={S.settingsCardBody}>
          <div style={S.settingsCardTitle}>Current storage key</div>
          <div style={S.settingsCardDesc}>
            Current key: <code style={S.code}>{CURRENT_KEY}</code>. Legacy keys are auto-migrated on load.
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <button style={{ ...S.smallBtn, background: c.infoBg, color: c.info, padding: "8px 16px", fontSize: 12 }}
              onClick={() => {
                const allKeys = Object.keys(localStorage).filter(k => k.startsWith("pl_"));
                showToast(`Found keys: ${allKeys.join(", ") || "none"}`);
              }}>
              🔍 Check storage keys
            </button>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <SectionTitle style={{ marginTop: 28 }}>🗑 Danger Zone</SectionTitle>
      <div style={{ ...S.settingsCard, borderColor: c.dangerBorder }}>
        <div style={S.settingsCardIcon}>🗑</div>
        <div style={S.settingsCardBody}>
          <div style={{ ...S.settingsCardTitle, color: c.dangerLight }}>Clear all data</div>
          <div style={S.settingsCardDesc}>Permanently deletes everything. Export a backup first!</div>
          <button style={{ ...S.submitBtn, background: c.badgeDueBg, color: c.dangerLight, border: `1px solid ${c.dangerBorder}`, marginTop: 14 }}
            onClick={() => {
              if (!window.confirm("Delete ALL data permanently? This cannot be undone. Export a backup first!")) return;
              save({ ...defaultState });
              setImportStatus({ type: "success", msg: "All data cleared." });
              showToast("All data cleared", "error");
            }}>
            🗑 Clear All Data
          </button>
        </div>
      </div>
    </div>
  );
}
